import { Modal, Table, Spinner, Badge } from 'react-bootstrap';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import dayjs from 'dayjs';
import { API } from '../../config/api';

const API_BASE = `${API.BACKEND}/api/v1`;

const fmtDur = sec => {
  const s = Math.max(0, Math.round(Number(sec) || 0));
  const h = Math.floor(s / 3600);
  const m = Math.round((s % 3600) / 60);
  if (h && m) return `${h}h ${m}m`;
  if (h) return `${h}h`;
  return `${m}m`;
};

/**
 * The individual drums behind one cell of the operator matrix.
 *
 * A number nobody can check is not trustworthy — it is merely unarguable. When
 * an operator disputes their speed this is the list it came from, drum by drum,
 * with the metres and running time that are theirs rather than the drum's.
 */
const DrillTokensModal = ({ cell, show, onHide }) => {
  const { data, isLoading } = useQuery({
    queryKey: ['drill-tokens', cell],
    queryFn: async () => {
      const params = {};
      if (cell?.operator) params.operator = cell.operator;
      if (cell?.productLabel) params.productLabel = cell.productLabel;
      if (cell?.phase) params.phase = cell.phase;
      if (cell?.machineId) params.machineId = cell.machineId;
      if (cell?.tandem !== undefined) params.tandem = cell.tandem;
      return (await axios.get(`${API_BASE}/production-metrics/drill-tokens`, { params })).data;
    },
    enabled: !!cell && show
  });

  const rows = data?.data || [];
  const totalM = rows.reduce((s, r) => s + (r.meters || 0), 0);
  const totalSec = rows.reduce((s, r) => s + (r.runningSec || 0), 0);
  const overall = totalSec > 0 ? (totalM / (totalSec / 60)).toFixed(1) : null;

  return (
    <Modal show={show} onHide={onHide} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title className="fs-8">
          {cell?.operator || 'All operators'}
          <span className="text-700 fs-9 ms-2">
            {cell?.productLabel} · {cell?.phase}
            {cell?.tandem ? ' (tandem)' : ''}
            {cell?.machineId ? ` · ${String(cell.machineId).toUpperCase()}` : ''}
          </span>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {isLoading ? (
          <div className="d-flex align-items-center gap-2 py-4">
            <Spinner animation="border" size="sm" /> <span className="fs-9">Loading…</span>
          </div>
        ) : !rows.length ? (
          <div className="text-center py-4 text-700 fs-9">No drums found for this cell.</div>
        ) : (
          <>
            <p className="fs-9 text-700">
              {rows.length} {rows.length === 1 ? 'drum' : 'drums'} · {Math.round(totalM)} m in{' '}
              {fmtDur(totalSec)}
              {overall ? (
                <>
                  {' '}
                  → <b className="text-primary">{overall} m/min</b>
                </>
              ) : null}
              . Metres and time shown are this operator&apos;s own share, not the whole drum.
            </p>

            <Table size="sm" className="fs-9 align-middle">
              <thead>
                <tr className="text-700">
                  <th>Finished</th>
                  <th>Drum</th>
                  <th>Job</th>
                  <th className="text-end">Their metres</th>
                  <th className="text-end">Drum total</th>
                  <th className="text-end">Running</th>
                  <th className="text-end">Speed</th>
                  <th>Shared with</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.tokenId}>
                    <td className="text-nowrap text-700">
                      {r.completedAt ? dayjs(r.completedAt).format('DD MMM, HH:mm') : '—'}
                    </td>
                    <td className="font-monospace fs-10">{r.tokenId}</td>
                    <td className="fs-10">
                      {r.jobCode || (r.jobId || '').slice(0, 8)}
                      {r.color ? <span className="text-700"> · {r.color}</span> : null}
                    </td>
                    <td className="text-end fw-semibold">{Math.round(r.meters)} m</td>
                    <td className="text-end text-700">
                      {Math.round(r.drumProducedLength)} m
                    </td>
                    <td className="text-end text-700">{fmtDur(r.runningSec)}</td>
                    <td className="text-end fw-semibold text-primary">
                      {r.speed != null ? `${r.speed}` : '—'}
                    </td>
                    <td>
                      {r.sharedWith?.length ? (
                        r.sharedWith.map(n => (
                          <Badge key={n} bg="secondary" className="fs-10 me-1">
                            {n}
                          </Badge>
                        ))
                      ) : (
                        <span className="text-700 fs-10">—</span>
                      )}
                      {r.editedBy ? (
                        <Badge
                          bg="warning"
                          className="fs-10"
                          title={`Attribution corrected by ${r.editedBy} on ${dayjs(r.editedAt).format('DD MMM YYYY, HH:mm')}`}
                        >
                          corrected
                        </Badge>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default DrillTokensModal;
